import React, { useState } from 'react';
import { View, TouchableOpacity, ViewStyle, FlatList, StyleProp, TextStyle } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from './MyAppText';
import { WindowInformation } from '../services/window-information';
import FontWeight from '../styles/fontWeights';

// Default color palette
export interface DefinedColorPickerProps {
    label?: string;
    labelStyle?: StyleProp<TextStyle>;
    labelFontSize?: number;
    colors: string[];
    textColors: string[];
    selectedColor?: string;
    onColorSelect?: (color: string) => void;
    onColorSelectText?: (color: string) => void;
    containerStyle?: ViewStyle;
    colorItemStyle?: ViewStyle;
}

export default function DefinedColorPicker({
    colors = [],
    textColors = [],
    selectedColor,
    onColorSelect,
    onColorSelectText,
    containerStyle,
    colorItemStyle,
    label,
    labelFontSize,
    labelStyle
}: DefinedColorPickerProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [internalSelectedColor, setInternalSelectedColor] = useState<string | null>(null);
    const currentSelectedColor = selectedColor !== undefined ? selectedColor : internalSelectedColor;
    const numColumns = windowInfo.isMobile ? 4 : 7;

    const handleColorSelect = (color: string) => {
        if (selectedColor === undefined) {
            setInternalSelectedColor(color);
        }
        onColorSelect?.(color);
        const index = colors.indexOf(color);
        onColorSelectText?.(textColors[index]);
    };

    const renderColorItem = ({ item: color }: { item: string; }) => {
        const isSelected = currentSelectedColor === color;

        return (
            <View style={styles.colorItemContainer}>
                <TouchableOpacity
                    style={[
                        styles.colorItem,
                        { backgroundColor: color },
                        isSelected && styles.selectedColorItem,
                        colorItemStyle,
                    ]}
                    onPress={() => handleColorSelect(color)}
                    activeOpacity={0.7}
                />
            </View>
        );
    };

    return (
        <View style={[styles.container, containerStyle]}>
            {label && <MyAppText style={labelStyle ? labelStyle : styles.label(labelFontSize ?? 14)}>{label}</MyAppText>}
            <FlatList
                data={colors}
                renderItem={renderColorItem}
                keyExtractor={(item, index) => `${item}-${index}-${numColumns}`}
                numColumns={numColumns}
                key={numColumns}
                scrollEnabled={false}
                contentContainerStyle={styles.gridContainer}
                columnWrapperStyle={styles.row}
            />
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    label: (fontSize: number) => ({
        color: theme.colors.neutral['500'],
        fontWeight: FontWeight.semiBold,
        fontSize,
    }),
    container: {
        justifyContent: 'space-around',
        rowGap: 8,
    },
    gridContainer: {
        justifyContent: 'space-around',
    },
    row: {
        justifyContent: 'space-around',
    },
    colorItemContainer: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    colorItem: {
        width: 32,
        height: 32,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        margin: 4,
    },
    selectedColorItem: {
        borderWidth: 3,
        borderColor: '#007AFF',
    },
}));
